import { useEffect, useRef } from 'react';
import { useSnackbar } from 'notistack';

type UseFocusSyncToastParams = {
    enabled?: boolean;
    dataUpdatedAt?: number;
    isRefetching: boolean;
    message: string;
};

const MIN_TOAST_GAP_MS = 5000;

export const useFocusSyncToast = ({
    enabled = true,
    dataUpdatedAt = 0,
    isRefetching,
    message,
}: UseFocusSyncToastParams) => {
    const { enqueueSnackbar } = useSnackbar();
    const hasInitialDataRef = useRef(false);
    const lastDataUpdatedAtRef = useRef(0);
    const focusTriggeredRef = useRef(false);
    const lastToastAtRef = useRef(0);

    useEffect(() => {
        if (!enabled || typeof window === 'undefined') {
            return;
        }

        const markFocusTriggered = () => {
            if (document.visibilityState === 'visible') {
                focusTriggeredRef.current = true;
            }
        };

        window.addEventListener('focus', markFocusTriggered);
        document.addEventListener('visibilitychange', markFocusTriggered);

        return () => {
            window.removeEventListener('focus', markFocusTriggered);
            document.removeEventListener('visibilitychange', markFocusTriggered);
        };
    }, [enabled]);

    useEffect(() => {
        if (!enabled || dataUpdatedAt <= 0) {
            return;
        }

        if (!hasInitialDataRef.current) {
            hasInitialDataRef.current = true;
            lastDataUpdatedAtRef.current = dataUpdatedAt;
            return;
        }

        if (dataUpdatedAt <= lastDataUpdatedAtRef.current || isRefetching) {
            return;
        }

        const now = Date.now();
        if (focusTriggeredRef.current && now - lastToastAtRef.current > MIN_TOAST_GAP_MS) {
            enqueueSnackbar(message, { variant: 'info' });
            lastToastAtRef.current = now;
        }

        focusTriggeredRef.current = false;
        lastDataUpdatedAtRef.current = dataUpdatedAt;
    }, [dataUpdatedAt, enabled, enqueueSnackbar, isRefetching, message]);
};
